import { deviceIsDesktop } from "../common/checkDevice";

export function sceneSignatureServes() {
  const trigger = document.querySelector(".scene-4");
  if (!trigger) return;
  if (trigger.dataset.bound === "1") return; // avoid double-binding per load
  trigger.dataset.bound = "1";

  gsap.registerPlugin(ScrollTrigger);

  // 🧹 extra safety: kill any existing ScrollTriggers for this scene
  // ScrollTrigger.getAll().forEach((st) => {
  //   const trg = st.trigger;
  //   if (trg === trigger || (typeof trg === "string" && trg === ".scene-4") || (trg && trg.classList && trg.classList.contains("scene-4"))) {
  //     st.kill();
  //   }
  // });

  // Split heading into chars once
  const split = new SplitText(".signature-serves h4", { types: "chars" });

  // Base states
  gsap.set(".signature-serves h4", { opacity: 1 });
  gsap.set(".scene-4 .articles_item_image", { opacity: 0, y: 10 });
  gsap.set(".scene-4 .articles_item_content_block", { opacity: 0, y: -10 });
  gsap.set(".scene-4 .content_text__items-outro", { opacity: 0, y: 20 });
  gsap.set(split.chars, { opacity: 0, y: 0 });

  const getDrinks = () => gsap.utils.toArray(".scene-4 .articles_item");

  // Dataset flags so they survive breakpoint changes (within same load)
  const markHeadingPlayed = () => {
    trigger.dataset.scene4HeadingPlayed = "1";
  };
  const markCompleted = () => {
    trigger.dataset.scene4Completed = "1";
  };

  const headingAlreadyPlayed = () => trigger.dataset.scene4HeadingPlayed === "1";
  const sceneAlreadyCompleted = () => trigger.dataset.scene4Completed === "1";

  const setFinalState = () => {
    gsap.set(split.chars, { opacity: 1, y: 0 });

    const drinks = getDrinks();
    gsap.set(
      drinks.map((d) => d.querySelector(".articles_item_image")),
      { opacity: 1, y: 0, pointerEvents: "auto" }
    );
    gsap.set(
      drinks.map((d) => d.querySelector(".articles_item_content_block")),
      { opacity: 1, y: 0, pointerEvents: "auto" }
    );

    gsap.set(".scene-4 .content_text__items-outro", {
      opacity: 1,
      y: 0,
    });
  };

  ScrollTrigger.matchMedia({
    // =============================
    // DESKTOP WIDTH (>= 768px)
    // =============================
    "(min-width: 768px)": () => {
      const isDesktopDevice = deviceIsDesktop();
      const isTouch = ScrollTrigger.isTouch === 1;
      const allowPinnedDesktop = isDesktopDevice && !isTouch;

      if (sceneAlreadyCompleted()) {
        setFinalState();
        return () => {};
      }

      // --- heading chars ---
      if (headingAlreadyPlayed()) {
        gsap.set(split.chars, { opacity: 1, y: 0 });
      } else {
        gsap.to(split.chars, {
          opacity: 1,
          duration: 0.01,
          stagger: 0.04,
          ease: "none",
          immediateRender: false,
          scrollTrigger: {
            trigger: ".signature-serves",
            start: "top center",
            toggleActions: "play none none none",
            once: true,
            onLeave: markHeadingPlayed,
            onLeaveBack: markHeadingPlayed,
          },
          onComplete: markHeadingPlayed,
        });
      }

      // ==========================
      // REAL DESKTOP (non-touch)
      // → pinned “step-through” drinks
      // ==========================
      if (allowPinnedDesktop) {
        const drinks = getDrinks();
        if (!drinks.length) return () => {};

        const PX_PER_SEGMENT = 500;
        const EXTRA_SCROLL = 500;

        const total = (drinks.length + 1) * PX_PER_SEGMENT + EXTRA_SCROLL;
        const thresholds = drinks.map((_, i) => (i * PX_PER_SEGMENT) / total);
        const outroThreshold = (drinks.length * PX_PER_SEGMENT) / total;

        const revealedDrinks = new Array(drinks.length).fill(false);
        let outroRevealed = false;

        function revealDrinkOnce(i) {
          if (revealedDrinks[i]) return;
          revealedDrinks[i] = true;

          const drink = drinks[i];
          if (!drink) return;

          const img = drink.querySelector(".articles_item_image");
          const txt = drink.querySelector(".articles_item_content_block");

          if (img) {
            gsap.to(img, {
              opacity: 1,
              y: 0,
              pointerEvents: "auto",
              duration: 0.55,
              ease: "none",
            });
          }
          if (txt) {
            gsap.to(txt, {
              opacity: 1,
              y: 0,
              pointerEvents: "auto",
              duration: 0.55,
              ease: "none",
            });
          }
        }

        function revealOutroOnce() {
          if (outroRevealed) return;
          outroRevealed = true;
          gsap.to(".scene-4 .content_text__items-outro", {
            opacity: 1,
            y: 0,
            duration: 0.55,
            ease: "none",
            onComplete: markCompleted,
          });
        }

        // main pin
        const pinST = ScrollTrigger.create({
          trigger: ".scene-4",
          start: "top top",
          end: () => `+=${drinks.length * PX_PER_SEGMENT}`,
          pin: true,
          scrub: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onEnter: () => revealDrinkOnce(0),
        });

        // progress watcher – NOTE: no more pinST.end reference
        const progressST = ScrollTrigger.create({
          trigger: ".scene-4",
          start: "top top",
          // use the same distance expression directly
          end: () => `+=${drinks.length * PX_PER_SEGMENT}`,
          scrub: true,
          onUpdate: (self) => {
            const prog = self.progress;

            for (let i = 0; i < thresholds.length; i++) {
              if (prog >= thresholds[i]) revealDrinkOnce(i);
            }

            if (prog >= outroThreshold) {
              revealOutroOnce();
            }
          },
        });

        return () => {
          pinST.kill();
          progressST.kill();
        };
      }

      // ==========================
      // WIDE BUT TOUCH (iPad etc.)
      // → use light “mobile-style” flow
      // ==========================
      const drinks = getDrinks();
      gsap.set(".scene-4 .articles_item_image", { opacity: 0, y: 10 });
      gsap.set(".scene-4 .articles_item_content_block", { opacity: 0, y: -10 });

      const itemTriggers = [];

      drinks.forEach((item) => {
        const img = item.querySelector(".articles_item_image");
        const txt = item.querySelector(".articles_item_content_block");

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: item,
            start: "top 60%",
            toggleActions: "play none none none",
            once: true,
          },
        });

        if (img) {
          tl.to(img, {
            opacity: 1,
            y: 0,
            duration: 0.45,
            ease: "power1.out",
          });
        }
        if (txt) {
          tl.to(
            txt,
            {
              opacity: 1,
              y: 0,
              duration: 0.45,
              ease: "power1.out",
            },
            "+=0.05"
          );
        }

        itemTriggers.push(tl);
      });

      const outroTween = gsap.to(".scene-4 .content_text__items-outro", {
        opacity: 1,
        y: 0,
        duration: 0.45,
        ease: "power1.out",
        scrollTrigger: {
          trigger: ".scene-4 .articles_items",
          start: "bottom 90%",
          toggleActions: "play none none none",
          once: true,
          onLeave: markCompleted,
          onLeaveBack: markCompleted,
        },
      });

      return () => {
        itemTriggers.forEach((tl) => {
          tl.scrollTrigger?.kill();
          tl.kill();
        });
        outroTween.scrollTrigger?.kill();
        outroTween.kill();
      };
    },

    // =============================
    // MOBILE WIDTH (< 768px)
    // =============================
    "(max-width: 767px)": () => {
      if (sceneAlreadyCompleted()) {
        setFinalState();
        return () => {};
      }

      // heading
      if (headingAlreadyPlayed()) {
        gsap.set(split.chars, { opacity: 1, y: 0 });
      } else {
        const hTl = gsap
          .timeline({
            scrollTrigger: {
              trigger: ".signature-serves",
              start: "top center",
              toggleActions: "play none none none",
              once: true,
              onLeave: markHeadingPlayed,
              onLeaveBack: markHeadingPlayed,
            },
          })
          .to(split.chars, {
            opacity: 1,
            duration: 0.01,
            stagger: 0.04,
            ease: "none",
          })
          .add(markHeadingPlayed);

        // optional cleanup if you want:
        // return () => { hTl.scrollTrigger?.kill(); hTl.kill(); }
      }

      // cards
      const items = getDrinks();
      gsap.set(".scene-4 .articles_item_image", { opacity: 0, y: 10 });
      gsap.set(".scene-4 .articles_item_content_block", { opacity: 0, y: -10 });

      const itemTriggers = [];

      items.forEach((item) => {
        const img = item.querySelector(".articles_item_image");
        const txt = item.querySelector(".articles_item_content_block");

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: item,
            start: "top 60%",
            toggleActions: "play none none none",
            once: true,
          },
        });

        if (img) {
          tl.to(img, {
            opacity: 1,
            y: 0,
            duration: 0.45,
            ease: "power1.out",
          });
        }

        if (txt) {
          tl.to(
            txt,
            {
              opacity: 1,
              y: 0,
              duration: 0.45,
              ease: "power1.out",
            },
            "+=0.05"
          );
        }

        itemTriggers.push(tl);
      });

      const outroTween = gsap.to(".scene-4 .content_text__items-outro", {
        opacity: 1,
        y: 0,
        duration: 0.45,
        ease: "power1.out",
        scrollTrigger: {
          trigger: ".scene-4 .articles_items",
          start: "bottom 90%",
          toggleActions: "play none none none",
          once: true,
          onLeave: markCompleted,
          onLeaveBack: markCompleted,
        },
      });

      return () => {
        itemTriggers.forEach((tl) => {
          tl.scrollTrigger?.kill();
          tl.kill();
        });
        outroTween.scrollTrigger?.kill();
        outroTween.kill();
      };
    },
  });
}
//
//
//
//
//
//
//
// ============================ CHANGED FOR MATCH MEDIA END . WORKING CODE BELOW BEFORE CHANGE
//
//
//
//
//
//
//

// import { deviceIsDesktop } from "../common/checkDevice";

// export function sceneSignatureServes() {
//   const trigger = document.querySelector(".scene-4");
//   if (!trigger) return;

//   const split = new SplitText(".signature-serves h4", { types: "chars" });

//   gsap.set(".signature-serves h4", { opacity: 1 });
//   gsap.set(".scene-4 .articles_item_image", { opacity: 0, y: 10 });
//   gsap.set(".scene-4 .articles_item_content_block", { opacity: 0, y: -10 });
//   gsap.set(".scene-4 .content_text__items-outro", { opacity: 0, y: 20 });

//   const isDesktop = deviceIsDesktop();

//   if (isDesktop) {
//     // IMPORTANT: start with chars hidden
//     gsap.set(split.chars, { opacity: 0, y: 0 });

//     // reveal on scroll (once)
//     gsap.to(split.chars, {
//       opacity: 1,
//       duration: 0.01,
//       stagger: 0.04,
//       ease: "none",
//       immediateRender: false,
//       scrollTrigger: {
//         trigger: ".signature-serves",
//         start: "top center", // or "top 70%" if you prefer later
//         toggleActions: "play none none none",
//         once: true,
//       },
//     });

//     const drinks = gsap.utils.toArray(".scene-4 .articles_item");
//     if (!drinks.length) return;

//     const PX_PER_SEGMENT = 500;
//     const EXTRA_SCROLL = 500; // scroll space after outro

//     // Pin to create the scroll space
//     const pinST = ScrollTrigger.create({
//       trigger: ".scene-4",
//       start: "top top",
//       end: () => `+=${drinks.length * PX_PER_SEGMENT}`,
//       pin: true,
//       scrub: true,
//       anticipatePin: 1,
//       invalidateOnRefresh: true,
//       // markers: true,
//       onEnter: () => revealDrinkOnce(0), // show first drink immediately
//     });

//     // Thresholds
//     const total = (drinks.length + 1) * PX_PER_SEGMENT + EXTRA_SCROLL;
//     const thresholds = drinks.map((_, i) => (i * PX_PER_SEGMENT) / total);
//     const outroThreshold = (drinks.length * PX_PER_SEGMENT) / total;

//     // Guards
//     const revealedDrinks = new Array(drinks.length).fill(false);
//     let outroRevealed = false;

//     function revealDrinkOnce(i) {
//       if (revealedDrinks[i]) return;
//       revealedDrinks[i] = true;
//       const drink = drinks[i];
//       if (!drink) return;

//       gsap.to(drink.querySelector(".articles_item_image"), {
//         opacity: 1,
//         y: 0,
//         pointerEvents: "auto",
//         duration: 0.55,
//         ease: "none",
//       });
//       gsap.to(drink.querySelector(".articles_item_content_block"), {
//         opacity: 1,
//         y: 0,
//         pointerEvents: "auto",
//         duration: 0.55,
//         ease: "none",
//       });
//     }

//     function revealOutroOnce() {
//       if (outroRevealed) return;
//       outroRevealed = true;
//       gsap.to(".scene-4 .content_text__items-outro", {
//         opacity: 1,
//         y: 0,
//         duration: 0.55,
//         ease: "none",
//       });
//     }

//     // Watch pin's progress
//     ScrollTrigger.create({
//       trigger: ".scene-4",
//       start: "top top",
//       end: () => pinST.end,
//       scrub: true,
//       onUpdate: (self) => {
//         const prog = self.progress;

//         // drinks
//         for (let i = 0; i < thresholds.length; i++) {
//           if (prog >= thresholds[i]) revealDrinkOnce(i);
//         }

//         // outro
//         if (prog >= outroThreshold) revealOutroOnce();
//       },
//     });
//   } else {
//     gsap.set(split.chars, { opacity: 0, y: 0 });

//     gsap
//       .timeline({
//         scrollTrigger: {
//           trigger: ".signature-serves",
//           start: "top center",
//           toggleActions: "play none none none",
//         },
//       })
//       .to(split.chars, {
//         opacity: 1,
//         duration: 0.01,
//         stagger: 0.04,
//         ease: "none",
//       });

//     // prep items
//     const items = gsap.utils.toArray(".scene-4 .articles_item");
//     gsap.set(".scene-4 .articles_item_image", { opacity: 0, y: 10 });
//     gsap.set(".scene-4 .articles_item_content_block", { opacity: 0, y: -10 });

//     // reveal each card when it enters the screen
//     items.forEach((item) => {
//       const img = item.querySelector(".articles_item_image");
//       const txt = item.querySelector(".articles_item_content_block");

//       const tl = gsap.timeline({
//         scrollTrigger: {
//           trigger: item,
//           start: "top 60%", // when the card nears viewport
//           toggleActions: "play none none none", // no reverse on scroll back
//           once: true, // fire only once
//           // markers: true,
//         },
//       });

//       if (img) {
//         tl.to(img, {
//           opacity: 1,
//           y: 0,
//           duration: 0.45,
//           ease: "power1.out",
//         });
//       }

//       if (txt) {
//         tl.to(
//           txt,
//           {
//             opacity: 1,
//             y: 0,
//             duration: 0.45,
//             ease: "power1.out",
//           },
//           "+=0.05"
//         ); // tiny gap after image
//       }
//     });

//     // outro once the list bottom hits viewport
//     gsap.to(".scene-4 .content_text__items-outro", {
//       opacity: 1,
//       y: 0,
//       duration: 0.45,
//       ease: "power1.out",
//       scrollTrigger: {
//         trigger: ".scene-4 .articles_items",
//         start: "bottom 90%",
//         toggleActions: "play none none none",
//         once: true,
//         // markers: true,
//       },
//     });
//   }
// }
